// TrainOnMeetingDialog — modale de couture Prep Meeting → Training (2026-05-16)
//
// Affiche les simulateurs disponibles avec :
//   - Hero = simulateur recommandé par l'IA (avec rationale)
//   - Liste des autres simulateurs accessibles
//   - Pattern upsell pour les simulateurs verrouillés selon profil :
//     * ORG/ADM/RH/COO → bouton "Activer cette licence" (lien Buy Pack)
//     * BNF/MNG → bouton "Demander à mon manager" (notif)
//
// Au clic "Démarrer", appelle l'endpoint qui crée un Target cas privé persistant
// puis déclenche onStartTraining côté parent (qui redirige vers TrainingPage).

import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles, Lock, ArrowRight, Loader2, Mail, ShoppingCart, AlertCircle } from "lucide-react";
import { authApi, trainingApi } from "../lib/api";
import type { AIAgent } from "../types";
import { useTranslation } from "react-i18next";
import { pickSimulatorDescription } from "../lib/simulator-description-i18n";
import { canBuyLicences, ACHAT_PACK_URL } from "../lib/encadrant";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  recommendedSimulatorCode: string | null;   // "discovery", "prospector", "arguwin"...
  simulatorRationale: string | null;
  userProfileType: string;                    // ADM | COO | ORG | MNG | RH | BNF
  meetingContext?: { companyName?: string; contactName?: string; meetingObjective?: string };
  // Callback enrichi (2026-05-17) pour reproduire le pattern Sales Coach :
  // le parent reçoit tous les champs nécessaires pour stocker le localStorage
  // bizcoach_pending_coaching_context lu par TrainingPage.
  onTargetCreated: (
    payload: {
      targetId: string;
      scenarioInstruction: string;
      suggestedDifficulty: "easy" | "medium" | "hard";
      offerId: string | null;
      personaId: string | null;
    },
    simulator: AIAgent,
  ) => void;
}

// Activer une licence = l'ACHETER : règle partagée `canBuyLicences` (ORG, ADM, GES — Q3 du
// 29-30/09/2026). La copie locale (ADM/COO/ORG/RH) envoyait le RH et le COO vers un achat que le
// serveur refuse. Les autres profils demandent à leur manager.
function canActivateLicence(profileType: string): boolean {
  return canBuyLicences(profileType);
}

// Mappe le code IA lowercase → match avec simulator.name (case insensitive)
function matchesSimulator(simulator: AIAgent, code: string): boolean {
  const n = (simulator.name || "").toLowerCase();
  return n === code || n.startsWith(code) || code.startsWith(n);
}

export function TrainOnMeetingDialog({
  open, onOpenChange, sessionId, recommendedSimulatorCode, simulatorRationale,
  userProfileType, meetingContext, onTargetCreated,
}: Props) {
  const [allAgents, setAllAgents] = useState<AIAgent[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [creating, setCreating] = useState<string | null>(null);   // simulatorId en cours de création
  const [requesting, setRequesting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  // Charge la liste des simulateurs au open
  useEffect(() => {
    if (!open) return;
    setLoadingAgents(true);
    setError(null);
    authApi.getAgents()
      .then((agents: AIAgent[]) => {
        // Filtre : uniquement les training agents (simulatorType==training), pas les coaches
        const trainingOnly = (agents || []).filter((a) => a.simulatorType !== "coach");
        setAllAgents(trainingOnly);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoadingAgents(false));
  }, [open]);

  // Sépare le simulateur recommandé du reste
  const recommended = recommendedSimulatorCode
    ? allAgents.find((a) => matchesSimulator(a, recommendedSimulatorCode))
    : null;
  const others = allAgents.filter((a) => !recommended || a.id !== recommended.id);

  async function handleStart(simulator: AIAgent) {
    setError(null);
    setCreating(simulator.id);
    try {
      const result = await trainingApi.startTrainingFromPrepMeeting(sessionId, simulator.id);
      onTargetCreated({
        targetId: result.targetId,
        scenarioInstruction: result.scenarioInstruction,
        suggestedDifficulty: result.suggestedDifficulty,
        offerId: result.offerId,
        personaId: result.personaId,
      }, simulator);
      onOpenChange(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(null);
    }
  }

  async function handleRequestAccess(simulator: AIAgent) {
    setError(null);
    setRequesting(simulator.id);
    try {
      const resp = await trainingApi.requestSimulatorAccess(simulator.id, {
        companyName: meetingContext?.companyName || "",
        contactName: meetingContext?.contactName || "",
        meetingObjective: meetingContext?.meetingObjective || "",
        simulatorName: simulator.name,
      });
      setConfirmation(resp.message);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRequesting(null);
    }
  }

  function handleActivateLicence() {
    // Même destination que tous les boutons d'achat (`ACHAT_PACK_URL`, correction n°5 du
    // 30/09/2026) : l'ancienne adresse `/buy-pack` était inconnue du routeur (retour accueil).
    onOpenChange(false);
    window.location.href = ACHAT_PACK_URL;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: "#384872" }}>
            <Sparkles className="h-5 w-5" style={{ color: "#988EF2" }} />
            S'entraîner sur ce RDV
          </DialogTitle>
        </DialogHeader>

        {loadingAgents && (
          <div className="text-center py-8">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3" style={{ color: "#988EF2" }} />
            <p className="text-sm" style={{ color: "#4B5573" }}>Chargement des simulateurs…</p>
          </div>
        )}

        {error && !loadingAgents && (
          <div className="rounded-lg p-3 flex items-start gap-2 text-sm" style={{ background: "#FFF5F5", color: "#C0423F" }}>
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>{error}</div>
          </div>
        )}

        {confirmation && (
          <div className="rounded-lg p-3 flex items-start gap-2 text-sm" style={{ background: "#F0FDF4", color: "#15803D" }}>
            <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>{confirmation}</div>
          </div>
        )}

        {!loadingAgents && (
          <div className="space-y-4 mt-2">
            {/* HERO — simulateur recommandé par l'IA */}
            {recommended && (
              <SimulatorCard
                agent={recommended}
                isHero
                rationale={simulatorRationale ?? null}
                onStart={() => handleStart(recommended)}
                onActivate={handleActivateLicence}
                onRequestAccess={() => handleRequestAccess(recommended)}
                creating={creating === recommended.id}
                requesting={requesting === recommended.id}
                canActivate={canActivateLicence(userProfileType)}
              />
            )}

            {/* Alternatives */}
            {others.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wider font-semibold mb-2" style={{ color: "#9CA3AF" }}>
                  {recommended ? "Ou autre simulateur" : "Choisissez un simulateur"}
                </div>
                <div className="space-y-2">
                  {others.map((agent) => (
                    <SimulatorCard
                      key={agent.id}
                      agent={agent}
                      onStart={() => handleStart(agent)}
                      onActivate={handleActivateLicence}
                      onRequestAccess={() => handleRequestAccess(agent)}
                      creating={creating === agent.id}
                      requesting={requesting === agent.id}
                      canActivate={canActivateLicence(userProfileType)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Carte simulateur individuelle — hero ou ligne compacte selon isHero
function SimulatorCard({
  agent, isHero, rationale, onStart, onActivate, onRequestAccess,
  creating, requesting, canActivate,
}: {
  agent: AIAgent;
  isHero?: boolean;
  rationale?: string | null;
  onStart: () => void;
  onActivate: () => void;
  onRequestAccess: () => void;
  creating: boolean;
  requesting: boolean;
  canActivate: boolean;
}) {
  const { t, i18n } = useTranslation();
  const locked = !agent.isUnlocked;
  const color = agent.color || "#988EF2";
  // Lot 7 i18n — description localisée (descriptionFr/En) avec fallback legacy.
  const localizedDescription = pickSimulatorDescription(agent, i18n.language);

  return (
    <div
      className={`rounded-xl border p-4 transition-all ${isHero ? "border-2" : "border"}`}
      style={{
        borderColor: isHero ? color : "#E5E7EB",
        background: isHero ? `linear-gradient(135deg, ${color}10 0%, ${color}05 100%)` : "white",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className={`rounded-xl flex items-center justify-center shrink-0 relative ${isHero ? "h-14 w-14" : "h-10 w-10"}`}
          style={{ background: color }}
        >
          {agent.image ? (
            <img src={agent.image} alt={agent.name} className={`rounded-xl object-cover ${isHero ? "h-14 w-14" : "h-10 w-10"}`} style={{ objectPosition: "top" }} />
          ) : (
            <span className={`text-white font-bold ${isHero ? "text-xl" : "text-base"}`}>{agent.icon || agent.name?.[0] || "?"}</span>
          )}
          {locked && (
            <div className="absolute -top-1 -right-1 rounded-full bg-white border h-5 w-5 flex items-center justify-center">
              <Lock className="h-3 w-3" style={{ color: "#6B7280" }} />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className={`font-bold ${isHero ? "text-base" : "text-sm"}`} style={{ color: "#384872" }}>
              {agent.name}
            </h3>
            {isHero && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white" style={{ background: color }}>
                ✨ Recommandé IA
              </span>
            )}
            {locked && (
              <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: "#F3F4F6", color: "#6B7280" }}>
                Non inclus
              </span>
            )}
          </div>
          {localizedDescription && (
            <p className="text-xs mt-1 line-clamp-2" style={{ color: "#4B5573" }}>
              {localizedDescription}
            </p>
          )}
          {isHero && rationale && (
            <p className="text-xs mt-2 italic" style={{ color: color }}>
              💡 {rationale}
            </p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="mt-3 flex justify-end gap-2 flex-wrap">
        {!locked && (
          <Button
            size="sm"
            disabled={creating}
            onClick={onStart}
            className="gap-1.5"
            style={{ background: color, color: "white" }}
          >
            {creating ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" />{t("salesCoach.trainOnMeeting.creating")}</>
            ) : (
              <>{t("salesCoach.trainOnMeeting.startTraining")} <ArrowRight className="h-3.5 w-3.5" /></>
            )}
          </Button>
        )}
        {locked && canActivate && (
          <Button size="sm" variant="outline" onClick={onActivate} className="gap-1.5">
            <ShoppingCart className="h-3.5 w-3.5" />
            {t("salesCoach.trainOnMeeting.activateLicence")}
          </Button>
        )}
        {locked && !canActivate && (
          <Button size="sm" variant="outline" disabled={requesting} onClick={onRequestAccess} className="gap-1.5">
            {requesting ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" />{t("salesCoach.trainOnMeeting.sending")}</>
            ) : (
              <><Mail className="h-3.5 w-3.5" />{t("salesCoach.trainOnMeeting.requestToManager")}</>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
